import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useAuth } from "@/_core/hooks/useAuth";
import { hasRole, hasAnyRole } from "@/lib/roles";
import { useFeatureEnabled } from "@/components/FeatureGuard";
import { 
  LayoutDashboard, LogOut, PanelLeft, BookOpen, Users, ClipboardList, 
  Home, UserPlus, History, GraduationCap, BarChart3, CalendarDays, MessageSquare, FileText,
  Dumbbell, Clock, Calendar, UserCheck, ChartBar, LayoutTemplate, Settings
} from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { useLocation } from "wouter";

// Flat menu structure - simpler and more compact
const MENU_ITEMS = [
  { icon: LayoutDashboard, label: "Übersicht", path: "/admin", roles: ["courseadmin", "admin"] },
  { icon: GraduationCap, label: "Meine Termine", path: "/admin/my-courses", roles: ["instructor"] },
  { icon: BookOpen, label: "Kurse", path: "/admin/courses", roles: ["courseadmin", "admin"] },
  { icon: CalendarDays, label: "Terminverwaltung", path: "/admin/sessions", roles: ["courseadmin", "admin"] },
  { icon: Users, label: "Kursleiter", path: "/admin/instructors", roles: ["courseadmin", "admin"] },
  { icon: ClipboardList, label: "Anmeldungen", path: "/admin/bookings", roles: ["courseadmin", "admin"] },
  { icon: History, label: "Buchungsverlauf", path: "/admin/booking-history", roles: ["courseadmin", "admin"] },
];

// Gym section - for gym management
const GYM_ITEMS = [
  { icon: Dumbbell, label: "Übersicht", path: "/admin/gym", roles: ["courseadmin", "admin"] },
  { icon: Clock, label: "Services", path: "/admin/gym/services", roles: ["admin"] },
  { icon: LayoutTemplate, label: "Slot-Vorlagen", path: "/admin/gym/templates", roles: ["admin"] },
  { icon: Calendar, label: "Slot-Kalender", path: "/admin/gym/slots", roles: ["courseadmin", "admin"] },
  { icon: ClipboardList, label: "Buchungen", path: "/admin/gym/bookings", roles: ["courseadmin", "admin"] },
  { icon: UserCheck, label: "Mitglieder", path: "/admin/gym/members", roles: ["admin"] },
  { icon: ChartBar, label: "Berichte & Statistiken", path: "/admin/gym/reports", roles: ["admin"] },
];

// Admin section - for administrative functions
const ADMIN_ITEMS = [
  { icon: BarChart3, label: "Berichte", path: "/admin/reports", roles: ["admin"] },
  { icon: MessageSquare, label: "Benachrichtigungen", path: "/admin/notifications", roles: ["admin"] },
  { icon: FileText, label: "Vorlagen", path: "/admin/templates", roles: ["admin"] },
  { icon: UserPlus, label: "Benutzer", path: "/admin/users", roles: ["admin"] },
  { icon: Settings, label: "Einstellungen", path: "/admin/settings", roles: ["admin"] },
];

const SIDEBAR_WIDTH_KEY = "admin-sidebar-width";
const DEFAULT_WIDTH = 260;
const MIN_WIDTH = 200;
const MAX_WIDTH = 400;

interface AdminSidebarProps {
  setSidebarWidth: (width: number) => void;
}

export function AdminSidebar({ setSidebarWidth }: AdminSidebarProps) {
  const { user, logout } = useAuth();
  const [location, setLocation] = useLocation();
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  
  // Feature flags
  const { enabled: gymEnabled } = useFeatureEnabled("gymEnabled");

  useEffect(() => {
    if (isCollapsed) {
      setIsResizing(false);
    }
  }, [isCollapsed]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const sidebarLeft = sidebarRef.current?.getBoundingClientRect().left ?? 0;
      const newWidth = e.clientX - sidebarLeft;
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => setIsResizing(false);

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, setSidebarWidth]);

  const visibleMenuItems = MENU_ITEMS.filter(item => hasAnyRole(user?.roles, item.roles as any));
  // Only show gym items if gymEnabled feature flag is true
  const visibleGymItems = gymEnabled 
    ? GYM_ITEMS.filter(item => hasAnyRole(user?.roles, item.roles as any))
    : [];
  const visibleAdminItems = ADMIN_ITEMS.filter(item => hasAnyRole(user?.roles, item.roles as any));

  return (
    <div className="relative" ref={sidebarRef}>
      <Sidebar collapsible="icon" className="border-r-0" disableTransition={isResizing}>
        <SidebarHeader className="h-16 justify-center">
          <div className="flex items-center gap-3 px-2 transition-all w-full" data-collapsed={isCollapsed}>
            <button
              onClick={toggleSidebar}
              className="h-8 w-8 flex items-center justify-center hover:bg-sidebar-accent rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring shrink-0"
              aria-label="Navigation umschalten"
            >
              <PanelLeft className="h-4 w-4 text-muted-foreground" />
            </button>
            {!isCollapsed && (
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-semibold tracking-tight truncate text-primary">
                  Admin
                </span>
              </div>
            )}
          </div>
        </SidebarHeader>

        <SidebarContent className="flex flex-col overflow-y-auto">
          {/* Main menu items - wrapped in block container */}
          <div className="block">
            <SidebarMenu className="px-2 py-1">
              {visibleMenuItems.map(item => {
                const isActive = location === item.path;
                return (
                  <SidebarMenuItem key={item.path}>
                    <SidebarMenuButton
                      isActive={isActive}
                      onClick={() => setLocation(item.path)}
                      tooltip={item.label}
                      className="h-10 transition-all font-normal"
                    >
                      <item.icon className={`h-4 w-4 ${isActive ? "text-primary" : ""}`} />
                      <span>{item.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </div>

          {/* Gym section - wrapped in block container, only shown if gymEnabled */}
          {visibleGymItems.length > 0 && (
            <div className="block border-t mt-4 pt-4">
              {!isCollapsed && (
                <p className="text-xs font-medium text-muted-foreground mb-2 px-3">
                  Fitnessstudio
                </p>
              )}
              <SidebarMenu className="px-2 py-1">
                {visibleGymItems.map(item => {
                  // Exact match for /admin/gym, startsWith for sub-paths
                  const isActive = item.path === "/admin/gym" 
                    ? location === item.path 
                    : location === item.path || location.startsWith(item.path + "/");
                  return (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton
                        isActive={isActive}
                        onClick={() => setLocation(item.path)}
                        tooltip={item.label}
                        className="h-10 transition-all font-normal"
                      >
                        <item.icon className={`h-4 w-4 ${isActive ? "text-primary" : ""}`} />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </div>
          )}

          {/* Admin section - wrapped in block container */}
          {visibleAdminItems.length > 0 && (
            <div className="block border-t mt-4 pt-4">
              {!isCollapsed && (
                <p className="text-xs font-medium text-muted-foreground mb-2 px-3">
                  Admin
                </p>
              )}
              <SidebarMenu className="px-2 py-1">
                {visibleAdminItems.map(item => {
                  const isActive = location === item.path;
                  return (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton
                        isActive={isActive}
                        onClick={() => setLocation(item.path)}
                        tooltip={item.label}
                        className="h-10 transition-all font-normal"
                      >
                        <item.icon className={`h-4 w-4 ${isActive ? "text-primary" : ""}`} />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </div>
          )}

          {/* Quick Actions - wrapped in block container */}
          <div className="block border-t mt-4 pt-4">
            {!isCollapsed && (
              <p className="text-xs font-medium text-muted-foreground mb-2 px-3">
                Schnellzugriff
              </p>
            )}
            <SidebarMenu className="px-2">
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => setLocation("/")}
                  tooltip="Zur Website"
                  className="h-9"
                >
                  <Home className="h-4 w-4" />
                  <span>Zur Website</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </div>
        </SidebarContent>

        <SidebarFooter className="p-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 rounded-lg px-1 py-1 hover:bg-accent/50 transition-colors w-full text-left group-data-[collapsible=icon]:justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <Avatar className="h-9 w-9 border shrink-0">
                  <AvatarFallback className="text-xs font-medium bg-primary/10 text-primary">
                    {user?.name?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                  <p className="text-sm font-medium truncate leading-none">
                    {user?.name || "-"}
                  </p>
                  <p className="text-xs text-muted-foreground truncate mt-1.5">
                    {hasRole(user?.roles, "admin") ? "Administrator" : hasRole(user?.roles, "courseadmin") ? "Kursadmin" : "Kursleiter"}
                  </p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem
                onClick={logout}
                className="cursor-pointer text-destructive focus:text-destructive"
              >
                <LogOut className="mr-2 h-4 w-4" />
                <span>Abmelden</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarFooter>
      </Sidebar>
      <div
        className={`absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-primary/20 transition-colors ${isCollapsed ? "hidden" : ""}`}
        onMouseDown={() => {
          if (isCollapsed) return;
          setIsResizing(true);
        }}
        style={{ zIndex: 50 }}
      />
    </div>
  );
}

// Export constants for use in parent components
export { SIDEBAR_WIDTH_KEY, DEFAULT_WIDTH };
